import { useTranslation } from 'react-i18next';

import { useFormat } from '@nucleus-commerce/i18n/react';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useNow } from '../hooks/use-now';
import { countdown, type Countdown } from '../schemas/auction-rules';

function useCountdownText() {
  const { t } = useTranslation('auctions');
  return (left: Countdown | null) => {
    if (!left) return t('countdown.over');
    if (left.days > 0) return t('countdown.days', { d: left.days, h: left.hours });
    if (left.hours > 0) {
      return t('countdown.hours', { h: left.hours, m: String(left.minutes).padStart(2, '0') });
    }
    return t('countdown.minutes', { m: left.minutes, s: String(left.seconds).padStart(2, '0') });
  };
}

/** Time left until `target` on this browser's clock, ticking every second. */
export function AuctionCountdown({ target, className }: { target: string; className?: string }) {
  const format = useFormat();
  const now = useNow();
  const text = useCountdownText();
  const left = countdown(target, now);
  return (
    <span
      className={cn('tabular-nums', className)}
      title={`${format.dateTime(target)} · ${format.utc(target)}`}
      data-testid="auction-countdown"
    >
      {text(left)}
    </span>
  );
}
